// FILE: src/components/questions/admin/InteractiveAnswers.js
//
// Question Editor support for the interactive (case) question types —
// enhanced_matching and ordered_sequence (PR B of the interactive-question
// workstream, 2026-07-16). The classic editor's choices UI assumes
// array-shaped choices; these types are object-shaped, so they get their own
// answers editor, payload builder, and validation on top of the canonical
// question-type registry.
//
// Form-state shapes (live in QuestionEditor formData):
//   matching: { prompts: [{id,text}], options: [{id,text}], pairs: {pid: oid} }
//   ordering: { items: [{id,text}], sequence: [id, ...] }  // sequence = KEY order

import React from 'react';
import { getQuestionType } from '../../../questionTypes/registry';

export const INTERACTIVE_TYPES = new Set(['enhanced_matching', 'ordered_sequence']);

const TYPE_LABELS = {
  enhanced_matching: 'Enhanced Matching (pair prompts with options)',
  ordered_sequence: 'Ordered Sequence (arrange in the correct order)',
};
export function interactiveTypeLabel(type) {
  return TYPE_LABELS[type] || type;
}

// ── Form-state builders ──────────────────────────────────────────────────────

/** formData slots from a stored question (null for classic types). */
export function interactiveFormState(q) {
  if (q?.type === 'enhanced_matching') {
    return {
      matching: {
        prompts: (q.choices?.prompts || []).map((p) => ({ id: String(p.id), text: p.text ?? '' })),
        options: (q.choices?.options || []).map((o) => ({ id: String(o.id), text: o.text ?? '' })),
        pairs: Object.fromEntries(
          (q.correctAnswers || []).map((pair) => String(pair).split(':')).filter((kv) => kv[0] && kv[1])
        ),
      },
      ordering: null,
    };
  }
  if (q?.type === 'ordered_sequence') {
    const items = (q.choices?.items || []).map((i) => ({ id: String(i.id), text: i.text ?? '' }));
    const key = (q.correctAnswers || []).map(String);
    return {
      matching: null,
      ordering: {
        items,
        // The key must be a permutation of item ids; repair defensively so a
        // malformed row is still editable rather than crashing the form.
        sequence: key.length === items.length && key.every((id) => items.some((i) => i.id === id))
          ? key
          : items.map((i) => i.id),
      },
    };
  }
  return { matching: null, ordering: null };
}

/** { choices, correctAnswers } to persist, from formData (any type). */
export function interactivePayload(formData) {
  if (formData.type === 'enhanced_matching' && formData.matching) {
    return {
      choices: {
        prompts: formData.matching.prompts.map((p) => ({ id: p.id, text: p.text })),
        options: formData.matching.options.map((o) => ({ id: o.id, text: o.text })),
      },
      correctAnswers: Object.entries(formData.matching.pairs)
        .map(([p, o]) => `${p}:${o}`)
        .sort(),
    };
  }
  if (formData.type === 'ordered_sequence' && formData.ordering) {
    return {
      choices: { items: formData.ordering.items.map((i) => ({ id: i.id, text: i.text })) },
      correctAnswers: [...formData.ordering.sequence],
    };
  }
  return { choices: formData.choices, correctAnswers: formData.correctAnswers };
}

/** Field errors for the interactive sections; {} when valid. */
export function validateInteractive(formData) {
  const errors = {};
  if (formData.type === 'enhanced_matching' && formData.matching) {
    const { prompts, options, pairs } = formData.matching;
    if (prompts.length < 2) errors.matching = 'Needs at least 2 prompts.';
    else if (options.length < 2) errors.matching = 'Needs at least 2 options.';
    else if ([...prompts, ...options].some((x) => !x.text || !x.text.trim())) {
      errors.matching = 'Every prompt and option needs text.';
    } else if (prompts.some((p) => !pairs[p.id])) {
      errors.matching = 'Every prompt needs a correct match.';
    } else if (Object.values(pairs).some((oid) => !options.some((o) => o.id === oid))) {
      errors.matching = 'A correct match points at a removed option.';
    }
  }
  if (formData.type === 'ordered_sequence' && formData.ordering) {
    const { items, sequence } = formData.ordering;
    if (items.length < 2) errors.ordering = 'Needs at least 2 items.';
    else if (items.some((i) => !i.text || !i.text.trim())) {
      errors.ordering = 'Every item needs text.';
    } else if (sequence.length !== items.length || !items.every((i) => sequence.includes(i.id))) {
      errors.ordering = 'The correct sequence must include every item exactly once.';
    }
  }
  // Belt-and-braces: run the canonical registry validation on the payload.
  if (!Object.keys(errors).length && INTERACTIVE_TYPES.has(formData.type)) {
    const spec = getQuestionType(formData.type);
    const regErrors = spec && typeof spec.validate === 'function'
      ? spec.validate({ ...interactivePayload(formData), type: formData.type })
      : [];
    if (regErrors.length) errors[formData.type === 'enhanced_matching' ? 'matching' : 'ordering'] = regErrors.join(' ');
  }
  return errors;
}

// ── ID helpers (stable, never reused within a question) ─────────────────────
function nextId(list, prefix) {
  const max = list.reduce((m, x) => {
    const n = parseInt(String(x.id).replace(/^[a-z]+/i, ''), 10);
    return Number.isInteger(n) ? Math.max(m, n) : m;
  }, 0);
  return `${prefix}${max + 1}`;
}

// ── UI ───────────────────────────────────────────────────────────────────────

const S = {
  group: { marginBottom: 18 },
  label: { display: 'block', fontWeight: 700, fontSize: '0.82rem', marginBottom: 6, color: '#333' },
  sub: { fontSize: '0.74rem', color: '#777', margin: '0 0 8px' },
  row: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 },
  input: { flex: 1, padding: '7px 9px', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.85rem' },
  idTag: { fontWeight: 700, fontSize: '0.74rem', color: '#555', minWidth: 26 },
  small: { padding: '4px 9px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: '0.78rem' },
  select: { padding: '6px 8px', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.82rem', maxWidth: '100%' },
  err: { color: '#c62828', fontSize: '0.78rem', marginTop: 4 },
};

function truncate(text, n = 70) {
  const s = String(text || '');
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

/**
 * The answers editor rendered in place of the classic choices block when the
 * selected question is an interactive type.
 *
 * @param {object}   formData     QuestionEditor form state (type + matching/ordering)
 * @param {function} patch        (updater) => setFormData(prev => updater(prev))
 * @param {object}   errors       validation errors ({ matching?, ordering? })
 */
export default function InteractiveAnswersEditor({ formData, patch, errors }) {
  if (formData.type === 'enhanced_matching' && formData.matching) {
    const { prompts, options, pairs } = formData.matching;
    const setM = (updater) => patch((prev) => ({ ...prev, matching: updater(prev.matching) }));
    return (
      <div data-testid="interactive-matching-editor">
        <div style={S.group}>
          <label style={S.label}>Prompts (the items to be matched)</label>
          {prompts.map((p, i) => (
            <div key={p.id} style={S.row}>
              <span style={S.idTag}>{p.id}</span>
              <input style={S.input} value={p.text} data-testid={`im-prompt-text-${p.id}`}
                onChange={(e) => setM((m) => ({ ...m, prompts: m.prompts.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) }))} />
              {prompts.length > 1 && (
                <button type="button" style={S.small} aria-label={`Remove prompt ${p.id}`} data-testid={`im-prompt-remove-${p.id}`}
                  onClick={() => setM((m) => {
                    const nextPairs = { ...m.pairs }; delete nextPairs[p.id];
                    return { ...m, prompts: m.prompts.filter((_, j) => j !== i), pairs: nextPairs };
                  })}>✕</button>
              )}
            </div>
          ))}
          <button type="button" style={S.small} data-testid="im-add-prompt"
            onClick={() => setM((m) => ({ ...m, prompts: [...m.prompts, { id: nextId(m.prompts, 'p'), text: '' }] }))}>
            + Add prompt
          </button>
        </div>

        <div style={S.group}>
          <label style={S.label}>Options (the match targets — reuse is allowed)</label>
          {options.map((o, i) => (
            <div key={o.id} style={S.row}>
              <span style={S.idTag}>{o.id}</span>
              <input style={S.input} value={o.text} data-testid={`im-option-text-${o.id}`}
                onChange={(e) => setM((m) => ({ ...m, options: m.options.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) }))} />
              {options.length > 1 && (
                <button type="button" style={S.small} aria-label={`Remove option ${o.id}`} data-testid={`im-option-remove-${o.id}`}
                  onClick={() => setM((m) => {
                    const nextPairs = Object.fromEntries(Object.entries(m.pairs).filter(([, oid]) => oid !== o.id));
                    return { ...m, options: m.options.filter((_, j) => j !== i), pairs: nextPairs };
                  })}>✕</button>
              )}
            </div>
          ))}
          <button type="button" style={S.small} data-testid="im-add-option"
            onClick={() => setM((m) => ({ ...m, options: [...m.options, { id: nextId(m.options, 'o'), text: '' }] }))}>
            + Add option
          </button>
        </div>

        <div style={S.group}>
          <label style={S.label}>Correct matches (the answer key)</label>
          <p style={S.sub}>Options may be used for more than one prompt — or not at all. The learner sees the full option text.</p>
          {prompts.map((p) => (
            <div key={p.id} style={S.row}>
              <span style={{ flex: 1, fontSize: '0.82rem' }}>{truncate(p.text || `(prompt ${p.id})`, 60)}</span>
              <select style={S.select} value={pairs[p.id] || ''} data-testid={`im-pair-${p.id}`}
                aria-label={`Correct match for ${p.id}`}
                onChange={(e) => setM((m) => {
                  const nextPairs = { ...m.pairs };
                  if (e.target.value) nextPairs[p.id] = e.target.value; else delete nextPairs[p.id];
                  return { ...m, pairs: nextPairs };
                })}>
                <option value="">— choose —</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>{o.id}: {truncate(o.text)}</option>
                ))}
              </select>
            </div>
          ))}
          {errors.matching && <div style={S.err} data-testid="editor-error-matching">{errors.matching}</div>}
        </div>
      </div>
    );
  }

  if (formData.type === 'ordered_sequence' && formData.ordering) {
    const { items, sequence } = formData.ordering;
    const byId = Object.fromEntries(items.map((i) => [i.id, i]));
    const setO = (updater) => patch((prev) => ({ ...prev, ordering: updater(prev.ordering) }));
    return (
      <div data-testid="interactive-ordering-editor">
        <div style={S.group}>
          <label style={S.label}>Items (as delivered to the learner)</label>
          {items.map((it, i) => (
            <div key={it.id} style={S.row}>
              <span style={S.idTag}>{it.id}</span>
              <input style={S.input} value={it.text} data-testid={`im-item-text-${it.id}`}
                onChange={(e) => setO((o) => ({ ...o, items: o.items.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) }))} />
              {items.length > 1 && (
                <button type="button" style={S.small} aria-label={`Remove item ${it.id}`} data-testid={`im-item-remove-${it.id}`}
                  onClick={() => setO((o) => ({
                    ...o,
                    items: o.items.filter((_, j) => j !== i),
                    sequence: o.sequence.filter((id) => id !== it.id),
                  }))}>✕</button>
              )}
            </div>
          ))}
          <button type="button" style={S.small} data-testid="im-add-item"
            onClick={() => setO((o) => {
              const id = nextId(o.items, 'i');
              return { ...o, items: [...o.items, { id, text: '' }], sequence: [...o.sequence, id] };
            })}>
            + Add item
          </button>
        </div>

        <div style={S.group}>
          <label style={S.label}>Correct sequence (the answer key — order matters)</label>
          {sequence.map((id, i) => (
            <div key={id} style={S.row}>
              <span style={S.idTag}>{i + 1}.</span>
              <span style={{ flex: 1, fontSize: '0.82rem' }}>{truncate(byId[id]?.text || `(item ${id})`, 80)}</span>
              <button type="button" style={S.small} disabled={i === 0} aria-label={`Move ${id} earlier`} data-testid={`im-seq-up-${id}`}
                onClick={() => setO((o) => {
                  const next = [...o.sequence]; [next[i - 1], next[i]] = [next[i], next[i - 1]];
                  return { ...o, sequence: next };
                })}>↑</button>
              <button type="button" style={S.small} disabled={i === sequence.length - 1} aria-label={`Move ${id} later`} data-testid={`im-seq-down-${id}`}
                onClick={() => setO((o) => {
                  const next = [...o.sequence]; [next[i], next[i + 1]] = [next[i + 1], next[i]];
                  return { ...o, sequence: next };
                })}>↓</button>
            </div>
          ))}
          {errors.ordering && <div style={S.err} data-testid="editor-error-ordering">{errors.ordering}</div>}
        </div>
      </div>
    );
  }

  return null;
}
